/**
 * DistanceInput Component
 * 
 * Distance input with miles/km unit selector for fitness events.
 * Stores internally in km and converts for display.
 */

import { useState } from 'react';

interface DistanceInputProps {
  label: string;
  value: number; // Always in km
  onChange: (distanceKm: number) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

export function DistanceInput({
  label,
  value,
  onChange,
  placeholder = "e.g. 5",
  required = false,
  className = ""
}: DistanceInputProps) {
  const [unit, setUnit] = useState<'km' | 'mi'>('km');
  
  // Convert km to miles: 1 km = 0.621371 miles
  const kmToMiles = (km: number) => km * 0.621371;
  const milesToKm = (miles: number) => miles / 0.621371;
  
  // Get display value in current unit
  const displayValue = unit === 'km' ? value : kmToMiles(value);
  
  const handleValueChange = (inputValue: string) => {
    const numValue = parseFloat(inputValue) || 0;
    const kmValue = unit === 'km' ? numValue : milesToKm(numValue);
    onChange(kmValue);
  };
  
  const handleUnitChange = (newUnit: 'km' | 'mi') => {
    setUnit(newUnit);
  };
  
  return (
    <div className={className}>
      <label htmlFor="distance" className="block text-sm font-medium text-text-secondary mb-2">
        {label} {required && '*'}
      </label>
      
      <div className="flex gap-2">
        {/* Distance Input */}
        <input
          type="text"
          inputMode="decimal"
          id="distance"
          value={displayValue > 0 ? Math.round(displayValue).toString() : ''}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 h-12 px-4 rounded-lg border bg-oro-surface border-oro-border text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none disabled:opacity-50"
        />
        
        {/* Unit Selector */}
        <div className="flex bg-oro-surface-alt border border-oro-border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => handleUnitChange('km')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              unit === 'km'
                ? 'bg-oro-gold text-oro-bg'
                : 'text-text-secondary hover:text-text-primary hover:bg-oro-surface'
            }`}
          >
            km
          </button>
          <button
            type="button"
            onClick={() => handleUnitChange('mi')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              unit === 'mi'
                ? 'bg-oro-gold text-oro-bg'
                : 'text-text-secondary hover:text-text-primary hover:bg-oro-surface'
            }`}
          >
            mi
          </button>
        </div>
      </div>
      
      <p className="mt-1 text-xs text-text-muted">
        Add distance for runs, hikes, bike rides, etc.
      </p>
    </div>
  );
}